import { PayloadAction } from '@reduxjs/toolkit'
import { SagaReturnType, call, put, select, takeLatest } from 'redux-saga/effects'

import waitingRoomModel, { CreateWaitingRoomDto, UpdateWaitingRoomDto, WaitingRoom } from '@/core/models/waiting-room.model'
import { SearchRequestOptions, SearchWaitingRoomsQuery } from '@/core/types/request'
import { SearchResponse } from '@/core/types/response'

import { updateConsultationSuccess as updateConsultationSuccessAction } from '@/redux/reducers/consultation.reducer'
import {
  createWaitingRoom as createWaitingRoomAction,
  createWaitingRoomSuccess as createWaitingRoomSuccessAction,
  createWaitingRoomFailure as createWaitingRoomFailureAction,
  listWaitingRooms as listWaitingRoomsAction,
  listWaitingRoomsSuccess as listWaitingRoomsSuccessAction,
  listWaitingRoomsFailure as listWaitingRoomsFailureAction,
  getWaitingRoom as getWaitingRoomAction,
  getWaitingRoomSuccess as getWaitingRoomSuccessAction,
  getWaitingRoomFailure as getWaitingRoomFailureAction,
  updateWaitingRoom as updateWaitingRoomAction,
  updateWaitingRoomSuccess as updateWaitingRoomSuccessAction,
  updateWaitingRoomFailure as updateWaitingRoomFailureAction,
  deleteWaitingRoom as deleteWaitingRoomAction,
  deleteWaitingRoomSuccess as deleteWaitingRoomSuccessAction,
  deleteWaitingRoomFailure as deleteWaitingRoomFailureAction,
  acceptConsultation as acceptConsultationAction,
  acceptConsultationSuccess as acceptConsultationSuccessAction,
  acceptConsultationFailure as acceptConsultationFailureAction,
  joinWaitingRoom as joinWaitingRoomAction,
  leaveWaitingRoom as leaveWaitingRoomAction,
} from '@/redux/reducers/waiting-room.reducer'

import { handleError } from '@/utils/errors'
import { Consultation } from '@/core/models/consultation.model'
import { selectAuthUser, setAuthUser } from '@/redux/reducers/auth.reducer'

function* createWaitingRoom(action: PayloadAction<CreateWaitingRoomDto>) {
  try {
    const waitingRoom: WaitingRoom = yield call(waitingRoomModel.createWaitingRoom, action.payload)
    const authUser: SagaReturnType<typeof selectAuthUser> = yield select(selectAuthUser)
    if (authUser) yield put(setAuthUser({ ...authUser, joinedWaitingRoom: { waitingRoom: waitingRoom._id, isAvailable: false, isOccupied: false } }))
    yield put(createWaitingRoomSuccessAction(waitingRoom))
  } catch (e) {
    yield put(createWaitingRoomFailureAction(handleError(e)))
  }
}

function* listWaitingRooms(action: PayloadAction<{ query: SearchWaitingRoomsQuery; options?: SearchRequestOptions }>) {
  try {
    const { query, options } = action.payload
    const { items, totalCount }: SearchResponse<WaitingRoom> = yield call(waitingRoomModel.listWaitingRooms, query, options)
    yield put(listWaitingRoomsSuccessAction({ items, totalCount }))
  } catch (e) {
    yield put(listWaitingRoomsFailureAction(handleError(e)))
  }
}

function* getWaitingRoom(action: PayloadAction<string>) {
  try {
    const waitingRoom: WaitingRoom = yield call(waitingRoomModel.getWaitingRoom, action.payload)
    yield put(getWaitingRoomSuccessAction(waitingRoom))
  } catch (e) {
    yield put(getWaitingRoomFailureAction(handleError(e)))
  }
}

function* updateWaitingRoom(action: PayloadAction<{ waitingRoomId: string; updateWaitingRoomDto: UpdateWaitingRoomDto }>) {
  try {
    const { waitingRoomId, updateWaitingRoomDto } = action.payload
    const waitingRoom: WaitingRoom = yield call(waitingRoomModel.updateWaitingRoom, waitingRoomId, updateWaitingRoomDto)
    yield put(updateWaitingRoomSuccessAction(waitingRoom))
  } catch (e) {
    yield put(updateWaitingRoomFailureAction(handleError(e)))
  }
}

function* deleteWaitingRoom(action: PayloadAction<string>) {
  try {
    yield call(waitingRoomModel.deleteWaitingRoom, action.payload)
    const authUser: SagaReturnType<typeof selectAuthUser> = yield select(selectAuthUser)
    if (authUser) yield put(setAuthUser({ ...authUser, joinedWaitingRoom: undefined }))
    yield put(deleteWaitingRoomSuccessAction(action.payload))
  } catch (e) {
    yield put(deleteWaitingRoomFailureAction(handleError(e)))
  }
}

function* acceptConsultation(action: PayloadAction<{ consultationId: string; doctorId: string; waitingRoomId: string }>) {
  try {
    const { consultation, waitingRoom }: { consultation: Consultation; waitingRoom: WaitingRoom } = yield call(
      waitingRoomModel.acceptConsultation,
      action.payload
    )
    yield put(acceptConsultationSuccessAction(waitingRoom))
    yield put(updateConsultationSuccessAction(consultation))
  } catch (e) {
    yield put(acceptConsultationFailureAction(handleError(e)))
  }
}

function* joinWaitingRoom(action: PayloadAction<ReturnType<typeof joinWaitingRoomAction>['payload']>) {
  try {
    const waitingRoomId = action.payload
    const waitingRoom: SagaReturnType<typeof waitingRoomModel.joinWaitingRoom> = yield call(waitingRoomModel.joinWaitingRoom, waitingRoomId)
    const authUser: SagaReturnType<typeof selectAuthUser> = yield select(selectAuthUser)
    if (authUser) yield put(setAuthUser({ ...authUser, joinedWaitingRoom: { waitingRoom: waitingRoom._id, isAvailable: false, isOccupied: false } }))
    yield put(updateWaitingRoomSuccessAction(waitingRoom))
  } catch (exception) {
    yield put(updateWaitingRoomFailureAction(handleError(exception)))
  }
}

function* leaveWaitingRoom(action: PayloadAction<ReturnType<typeof leaveWaitingRoomAction>['payload']>) {
  try {
    const waitingRoomId = action.payload
    const waitingRoom: SagaReturnType<typeof waitingRoomModel.leaveWaitingRoom> = yield call(waitingRoomModel.leaveWaitingRoom, waitingRoomId)
    const authUser: SagaReturnType<typeof selectAuthUser> = yield select(selectAuthUser)
    if (authUser) yield put(setAuthUser({ ...authUser, joinedWaitingRoom: undefined }))
    yield put(updateWaitingRoomSuccessAction(waitingRoom))
  } catch (exception) {
    yield put(updateWaitingRoomFailureAction(handleError(exception)))
  }
}

const watchWaitingRoomSagas = [
  takeLatest(createWaitingRoomAction.type, createWaitingRoom),
  takeLatest(listWaitingRoomsAction.type, listWaitingRooms),
  takeLatest(getWaitingRoomAction.type, getWaitingRoom),
  takeLatest(updateWaitingRoomAction.type, updateWaitingRoom),
  takeLatest(deleteWaitingRoomAction.type, deleteWaitingRoom),
  takeLatest(acceptConsultationAction.type, acceptConsultation),
  takeLatest(joinWaitingRoomAction.type, joinWaitingRoom),
  takeLatest(leaveWaitingRoomAction.type, leaveWaitingRoom),
]

export default watchWaitingRoomSagas
